export type LogLevel = "debug" | "info" | "warn" | "error";
export type LogSource = "server" | "client";

export interface LogEvent {
  timestamp: string;
  level: LogLevel;
  message: string;
  source: LogSource;
  service: string;
  environment?: string;
  url?: string;
  stack?: string;
  context?: Record<string, unknown>;
}

export interface LogDrainConfig {
  url: string;
  headers?: Record<string, string>;
}

export interface OTelConfig {
  endpoint: string;
  headers?: Record<string, string>;
  resource?: Record<string, string>;
}

export interface SentryConfig {
  dsn: string;
  environment?: string;
  release?: string;
}

export interface LoggerConfig {
  service: string;
  source: LogSource;
  environment?: string;
  level?: LogLevel;
  console?: boolean;
  drain?: LogDrainConfig;
  otel?: OTelConfig;
  sentry?: SentryConfig;
}

export interface Logger {
  debug: (message: string, context?: Record<string, unknown>) => void;
  info: (message: string, context?: Record<string, unknown>) => void;
  warn: (message: string, context?: Record<string, unknown>) => void;
  error: (
    message: string,
    error?: Error | unknown,
    context?: Record<string, unknown>
  ) => void;
  captureError: (error: Error | unknown, context?: Record<string, unknown>) => void;
}

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const OTEL_SEVERITY: Record<LogLevel, number> = {
  debug: 5,
  info: 9,
  warn: 13,
  error: 17,
};

export function createLogger(config: LoggerConfig): Logger {
  const level = config.level ?? "info";
  const shouldConsole =
    config.console ??
    (config.environment
      ? ["dev", "development", "local"].includes(config.environment)
      : false);

  const emit = (event: LogEvent, error?: Error) => {
    if (LEVEL_ORDER[event.level] < LEVEL_ORDER[level]) return;

    if (shouldConsole) {
      const payload = formatConsole(event, error);
      if (event.level === "error") console.error(...payload);
      else if (event.level === "warn") console.warn(...payload);
      else if (event.level === "debug") console.debug(...payload);
      else console.info(...payload);
    }

    if (config.drain) void sendDrain(config.drain, event);
    if (config.otel) void sendOtel(config.otel, event, error);
    if (config.sentry && event.level === "error") {
      void sendSentry(config.sentry, event, error);
    }
  };

  const baseEvent = (levelValue: LogLevel, message: string, context?: Record<string, unknown>): LogEvent => {
    return {
      timestamp: new Date().toISOString(),
      level: levelValue,
      message,
      source: config.source,
      service: config.service,
      environment: config.environment,
      url: context?.url as string | undefined,
      stack: context?.stack as string | undefined,
      context: sanitizeContext(context),
    };
  };

  return {
    debug: (message, context) => emit(baseEvent("debug", message, context)),
    info: (message, context) => emit(baseEvent("info", message, context)),
    warn: (message, context) => emit(baseEvent("warn", message, context)),
    error: (message, error, context) => {
      const err = error instanceof Error ? error : undefined;
      const mergedContext = {
        ...context,
        errorMessage: err?.message ?? (error ? String(error) : undefined),
      };
      emit(
        {
          ...baseEvent("error", message, mergedContext),
          stack: err?.stack ?? (context?.stack as string | undefined),
        },
        err
      );
    },
    captureError: (error, context) => {
      const err = error instanceof Error ? error : undefined;
      const message = err?.message ?? (error ? String(error) : "Unknown error");
      const mergedContext = {
        ...context,
        errorMessage: err?.message ?? (error ? String(error) : undefined),
        stack: err?.stack,
      };
      emit(baseEvent("error", message, mergedContext), err);
    },
  };
}

function sanitizeContext(context?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!context) return undefined;
  return Object.fromEntries(
    Object.entries(context).map(([key, value]) => {
      if (value instanceof Error) {
        return [key, { name: value.name, message: value.message, stack: value.stack }];
      }
      return [key, value];
    })
  );
}

function formatConsole(event: LogEvent, error?: Error): unknown[] {
  const summary = `[${event.service}] ${event.level.toUpperCase()}: ${event.message}`;
  const meta = {
    source: event.source,
    environment: event.environment,
    url: event.url,
    context: event.context,
  };

  if (error) return [summary, error, meta];
  return [summary, meta];
}

async function sendDrain(config: LogDrainConfig, event: LogEvent) {
  try {
    await fetch(config.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(config.headers ?? {}),
      },
      body: JSON.stringify(event),
    });
  } catch {
    // ignore
  }
}

async function sendOtel(config: OTelConfig, event: LogEvent, error?: Error) {
  const timeUnixNano = `${Date.now()}000000`;
  const attributes = toOtelAttributes({
    "service.name": event.service,
    "service.environment": event.environment,
    source: event.source,
    url: event.url,
    ...(event.context ?? {}),
  });

  if (error) {
    attributes.push({
      key: "error.stack",
      value: { stringValue: error.stack ?? "" },
    });
  }

  const resourceAttributes = toOtelAttributes({
    "service.name": event.service,
    ...(config.resource ?? {}),
  });

  const payload = {
    resourceLogs: [
      {
        resource: {
          attributes: resourceAttributes,
        },
        scopeLogs: [
          {
            scope: { name: "@airc/logger", version: "1.0.0" },
            logRecords: [
              {
                timeUnixNano,
                severityText: event.level.toUpperCase(),
                severityNumber: OTEL_SEVERITY[event.level],
                body: { stringValue: event.message },
                attributes,
              },
            ],
          },
        ],
      },
    ],
  };

  try {
    await fetch(config.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(config.headers ?? {}),
      },
      body: JSON.stringify(payload),
    });
  } catch {
    // ignore
  }
}

function toOtelAttributes(data: Record<string, unknown | undefined>) {
  const attributes: Array<{ key: string; value: { stringValue: string } }> = [];
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string") {
      attributes.push({ key, value: { stringValue: value } });
    } else {
      attributes.push({ key, value: { stringValue: JSON.stringify(value) } });
    }
  }
  return attributes;
}

async function sendSentry(config: SentryConfig, event: LogEvent, error?: Error) {
  const dsn = parseSentryDsn(config.dsn);
  if (!dsn) return;

  const eventId = generateEventId();
  const payload = {
    event_id: eventId,
    timestamp: event.timestamp,
    platform: "javascript",
    level: mapSentryLevel(event.level),
    logger: event.service,
    message: event.message,
    environment: config.environment ?? event.environment,
    release: config.release,
    tags: {
      source: event.source,
      service: event.service,
    },
    extra: {
      ...(event.context ?? {}),
      stack: event.stack,
    },
  } as Record<string, unknown>;

  if (error) {
    payload.exception = {
      values: [
        {
          type: error.name,
          value: error.message,
          stacktrace: error.stack,
        },
      ],
    };
  }

  const envelope = [
    JSON.stringify({ event_id: eventId, sent_at: new Date().toISOString() }),
    JSON.stringify({ type: "event" }),
    JSON.stringify(payload),
  ].join("\n");

  try {
    await fetch(dsn.envelopeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-sentry-envelope",
        "X-Sentry-Auth": `Sentry sentry_version=7, sentry_key=${dsn.publicKey}, sentry_client=airc-logger/1.0`,
      },
      body: envelope,
    });
  } catch {
    // ignore
  }
}

function mapSentryLevel(level: LogLevel) {
  switch (level) {
    case "debug":
      return "debug";
    case "info":
      return "info";
    case "warn":
      return "warning";
    default:
      return "error";
  }
}

function parseSentryDsn(dsn: string) {
  try {
    const url = new URL(dsn);
    const publicKey = url.username;
    const projectId = url.pathname.replace("/", "");
    const host = url.host;
    return {
      publicKey,
      envelopeUrl: `${url.protocol}//${host}/api/${projectId}/envelope/`,
    };
  } catch {
    return null;
  }
}

function generateEventId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`;
}
